import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import StatusPill from "ui-components/status-pill/statusPill";
import TimestampDisplay from "ui-components/timestamp-display/timestampDisplay";
import DockUserCard from "ui-components/dock-user-card/dockUserCard";
import EquipmentCard from "ui-components/equipment-card/equipmentCard";
import {
  formatDoorDisplay,
  formatFreightBarcode,
  formatSessionDuration,
  humanizeEventType,
} from "app/data/dock-scan/dockLogMappers";
import { AuthAuditEvent, ScanLog } from "app/data/dock-scan/models";
import { DataTableColumnOption } from "ui-components/data-table/dataTableTypes";
import ScanResultTag from "./components/ScanResultTag";

const scanLogHelper = createColumnHelper<ScanLog>();
const authHelper = createColumnHelper<AuthAuditEvent>();

export const DOCK_SCAN_LOG_COLUMN_OPTIONS: DataTableColumnOption[] = [
  { id: "time", label: "Time" },
  { id: "barcodeValue", label: "Barcode" },
  { id: "barcodeType", label: "Type" },
  { id: "eventType", label: "Event" },
  { id: "resultStatus", label: "Result" },
  { id: "employeeId", label: "Operator" },
  { id: "equipmentId", label: "Equipment" },
  { id: "door", label: "Recommended Location" },
  { id: "manifest", label: "Manifest / Picklist" },
  { id: "workflowType", label: "Workflow" },
  { id: "resultMessage", label: "Message" },
];

export const DOCK_SCAN_LOG_COLUMNS_HIDDEN_BY_DEFAULT = ["workflowType", "resultMessage"];

export function getDockScanLogColumns(options?: { showOperator?: boolean }) {
  const cols = [
    scanLogHelper.accessor((row) => row.scannedAt || row.createdAt, {
      id: "time",
      header: "Time",
      cell: (props) => (
        <TimestampDisplay value={props.getValue()} format="relative" />
      ),
      size: 120,
      minSize: 110,
    }),
    scanLogHelper.accessor("barcodeValue", {
      id: "barcodeValue",
      header: "Barcode",
      cell: (props) => (
        <code className="dock-users__mono">{formatFreightBarcode(props.getValue())}</code>
      ),
      size: 150,
      minSize: 130,
    }),
    scanLogHelper.accessor("barcodeType", {
      id: "barcodeType",
      header: "Type",
      size: 110,
      minSize: 100,
    }),
    scanLogHelper.accessor("eventType", {
      id: "eventType",
      header: "Event",
      cell: (props) => humanizeEventType(props.getValue()),
      size: 130,
      minSize: 110,
    }),
    scanLogHelper.accessor("resultStatus", {
      id: "resultStatus",
      header: "Result",
      cell: (props) => <ScanResultTag status={props.getValue()} />,
      size: 120,
      minSize: 110,
    }),
    scanLogHelper.accessor("employeeId", {
      id: "employeeId",
      header: "Operator",
      cell: (props) => {
        const row = props.row.original;
        return (
          <DockUserCard
            employeeId={props.getValue()}
            firstName={row.employee?.firstName}
            lastName={row.employee?.lastName}
            keepTooltipInside={false}
          />
        );
      },
      size: 180,
      minSize: 160,
    }),
    scanLogHelper.accessor("equipmentId", {
      id: "equipmentId",
      header: "Equipment",
      cell: (props) => {
        const id = props.getValue();
        if (!id) return "—";
        return <EquipmentCard unitNumber={id} keepTooltipInside={false} />;
      },
      size: 120,
      minSize: 100,
    }),
    scanLogHelper.accessor("recommendedDoorId", {
      id: "door",
      header: "Recommended Location",
      cell: (props) => formatDoorDisplay(props.getValue()) || "—",
      size: 160,
      minSize: 140,
    }),
    scanLogHelper.accessor("manifestNumber", {
      id: "manifest",
      header: "Manifest / Picklist",
      cell: (props) => {
        const row = props.row.original;
        if (!row.manifestNumber && !row.picklistNumber) return "—";
        const parts = [];
        if (row.manifestNumber) parts.push(`M#${row.manifestNumber}`);
        if (row.picklistNumber) parts.push(`PL#${row.picklistNumber}`);
        return parts.join(" · ");
      },
      size: 140,
      minSize: 120,
    }),
    scanLogHelper.accessor("workflowType", {
      id: "workflowType",
      header: "Workflow",
      cell: (props) => props.getValue()?.replace(/_/g, " ") || "—",
      size: 140,
      minSize: 120,
    }),
    scanLogHelper.accessor("resultMessage", {
      id: "resultMessage",
      header: "Message",
      cell: (props) => props.getValue() || props.row.original.resultCode || "—",
      size: 180,
      minSize: 140,
    }),
  ];

  if (options?.showOperator === false) {
    return cols.filter((c) => c.id !== "employeeId" && c.id !== "equipmentId");
  }
  return cols;
}

export const DOCK_AUTH_EVENT_COLUMN_OPTIONS: DataTableColumnOption[] = [
  { id: "createdAt", label: "Time" },
  { id: "eventType", label: "Event" },
  { id: "equipmentId", label: "Equipment" },
  { id: "message", label: "Message" },
];

export function getDockAuthEventColumns() {
  return [
    authHelper.accessor("createdAt", {
      id: "createdAt",
      header: "Time",
      cell: (props) => <TimestampDisplay value={props.getValue()} format="relative" />,
      size: 120,
    }),
    authHelper.accessor("eventType", {
      id: "eventType",
      header: "Event",
      cell: (props) => {
        const type = props.getValue();
        const isFailure = type.includes("DENIED");
        return (
          <StatusPill
            label={type.replace(/_/g, " ")}
            variant={isFailure ? "failure" : type === "LOGIN_SUCCESS" ? "success" : "neutral"}
          />
        );
      },
      size: 180,
    }),
    authHelper.accessor("equipmentId", {
      id: "equipmentId",
      header: "Equipment",
      cell: (props) => {
        const id = props.getValue();
        if (!id) return "—";
        return <EquipmentCard unitNumber={id} keepTooltipInside={false} />;
      },
      size: 120,
    }),
    authHelper.accessor("message", {
      id: "message",
      header: "Message",
      cell: (props) => props.getValue() || "—",
      size: 200,
    }),
  ];
}

export function ScanCountTags({
  total,
  success,
  failure,
}: {
  total?: number;
  success?: number;
  failure?: number;
}) {
  if (total == null) return <span>—</span>;
  return (
    <div className="dock-users__scan-tags">
      <span className="dock-users__scan-total">{total}</span>
      <div className="dock-users__scan-pills">
        <StatusPill label={`SUCCESS ${success ?? 0}`} variant="success" />
        <StatusPill label={`FAILURE ${failure ?? 0}`} variant="failure" />
      </div>
    </div>
  );
}

export function SessionDurationCell({ ms, startedAt }: { ms?: number; startedAt?: string }) {
  if (ms == null && !startedAt) return <span>—</span>;
  return (
    <div>
      {ms != null && <div>{formatSessionDuration(ms)}</div>}
      {startedAt && (
        <TimestampDisplay value={startedAt} format="relative" className="text-light" />
      )}
    </div>
  );
}
